import { Alert, Grid } from "@mui/material";
import { EntityWithIdFields, QueryStatus } from "src/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useContext, useEffect } from "react";
import { useLoader } from "src/hooks/useLoader";
import { ContextOperacion, ContextOperacionType } from "../../RefactorLayout";
import { useOperacionCLFB } from "./hooks/useOperacionCLFB";
import SeccionCLFBDatosOperacion from "./components/SeccionCLFBDatosOperacion";
import toast from "react-hot-toast";
import { HttpCLFB } from "./api/HttpCLFB";
import {FormOperacionCLFB, OperacionCLFB, schemaFormOperacionCLFB} from "./types/types";
import SeccionCLFBDetalle from "./components/SeccionCLFBDetalle";
import {SeccionFirmas} from "../../components/SeccionFirmas";
import SeccionCLFBDatosAdicionales from "./components/SeccionCLFBDatosAdicionales";
import {SistemaTipo} from "../../../../types/enum/enum";

type SistemaCLFBarantiaContentProps = {
    operacion: OperacionCLFB,
}

const SistemaCLFBContent = ({ operacion }: SistemaCLFBarantiaContentProps) => {

    const { setSavePromise, permisoTipo } = useContext<ContextOperacionType>(ContextOperacion)

    const formMethods = useForm<FormOperacionCLFB>({
        resolver: zodResolver(schemaFormOperacionCLFB),
        defaultValues: { ...operacion }
    })

    const { showLoader, hideLoader } = useLoader()

    useEffect(() => {
        if (operacion.permiteEditarOperacion) {
            const handleSave: SubmitHandler<FormOperacionCLFB> = (data) => {
                showLoader()
                const promise = HttpCLFB.put(operacion.id, data)

                toast.promise(promise, {
                    loading: 'Guardando.',
                    success: 'Guardado correctamente.',
                    error: e => `${e}`
                }).finally(hideLoader)
            }

            const onSave = () => formMethods.handleSubmit(handleSave)
            setSavePromise(onSave)
        }
    }, [])

    return (
        <>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <SeccionCLFBDatosOperacion
                        formMethods={formMethods}
                        operacion={operacion}
                        readOnly={!operacion.permiteEditarOperacion}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SeccionCLFBDetalle
                        codPais={operacion.codPais}
                        idOperacion={operacion[EntityWithIdFields.Id]}
                        permiteEditar={permisoTipo > 1}
                        permiteEliminar={false}
                        adicionales={
                            <SeccionCLFBDatosAdicionales
                                formMethods={formMethods}
                                codPais={operacion.codPais}
                                readOnly={!(permisoTipo > 1 && operacion.permiteEditarOperacion)}
                                operacion={operacion}
                            />
                        }/>
                </Grid>
                <Grid item xs={12}>
                    <Grid item xs={12}>
                        <SeccionFirmas
                            codSistema={SistemaTipo.CLFB}
                            idOperacion={operacion[EntityWithIdFields.Id]}
                        />
                    </Grid>
                </Grid>

            </Grid>
        </>
    )
}


const SistemaCLFB = () => {

    const { idOperacion, setSavePromise } = useContext<ContextOperacionType>(ContextOperacion)

    const { status, operacion } = useOperacionCLFB(idOperacion)

    const { showLoader, hideLoader } = useLoader()

    useEffect(() => {
        if (status === QueryStatus.Loading) {
            showLoader()
        } else {
            hideLoader();
        }
    }, [status])

    if (!operacion) {
        return (
            <></>
        )
    }

    if (status === QueryStatus.Error) {
        return (
            <Alert severity='error'>
                Se produjo un error al cargar la operación.
            </Alert>
        )
    }

    return (
        <SistemaCLFBContent
            operacion={operacion}
        />
    )
}

export default SistemaCLFB;
